import { ref } from 'vue';
import { $fetch } from '@/utils/fetch';
import type { ExamStatus, MyExamRow } from '@/views/user/user-data';

interface RegSession {
  exam_time: string; // "YYYY-MM-DD HH:mm:ss-HH:mm:ss"
  capacity: number;
}

interface RegExam {
  name: string;
}

interface RegSite {
  name: string;
  location: string;
  traffic_info: string;
  contact_info: string;
}

/** 报名时的报考人信息快照（与当前用户信息可能不同） */
interface RegProfile {
  real_name: string;
  cert_type: string;
  cert_no: string;
  phone: string;
  email: string;
  role: string;
  org: string;
  school: string;
}

interface RegItem {
  id: number | string;
  audit_status: number;
  canceled: number;
  /** 审核拒绝时的驳回原因 */
  remark?: string;
  registered_at: string;
  session: RegSession;
  exam: RegExam;
  site: RegSite;
  registration_profile?: RegProfile;
}

interface RegListData {
  list: RegItem[];
  total: number;
  page: number;
  page_size: number;
}

// 状态映射：canceled 优先；audit_status 1=待审核 2=已通过 3=已拒绝 4=已取消；canceled 1=未取消 2=已取消
function mapStatus(audit: number, canceled: number): { status: ExamStatus, statusText: string } {
  if (canceled === 2 || audit === 4) {
    return { status: 'canceled', statusText: '已取消' };
  }

  switch (audit) {
    case 2:
      return { status: 'approved', statusText: '已通过' };
    case 3:
      return { status: 'rejected', statusText: '已拒绝' };
    default:
      return { status: 'reviewing', statusText: '待审核' };
  }
}

function mapReg(item: RegItem): MyExamRow {
  const { status, statusText } = mapStatus(item.audit_status, item.canceled);
  // exam_time 形如 "2026-09-12 09:00:00-11:00:00"
  const [datePart, rangePart] = (item.session?.exam_time ?? '').split(' ');
  const [start, end] = (rangePart ?? '').split('-');

  return {
    id: String(item.id),
    subject: item.exam?.name ?? '',
    location: item.site?.name ?? '',
    time: `${datePart ?? ''} ${start?.slice(0, 5) ?? ''}–${end?.slice(0, 5) ?? ''}`.trim(),
    status,
    statusText,
    remark: item.remark ?? '',
    siteAddress: item.site?.location ?? '',
    trafficInfo: item.site?.traffic_info ?? '',
    contactInfo: item.site?.contact_info ?? '',
    realName: item.registration_profile?.real_name ?? '',
    certType: item.registration_profile?.cert_type ?? '',
    certNo: item.registration_profile?.cert_no ?? '',
    phone: item.registration_profile?.phone ?? '',
    email: item.registration_profile?.email ?? '',
    role: item.registration_profile?.role ?? '',
    org: item.registration_profile?.org ?? '',
    school: item.registration_profile?.school ?? '',
  };
}

// 模块级单例：列表页与详情页共享同一份数据，避免重复拉取全量列表
const myExams = ref<MyExamRow[]>([]);
const loading = ref(false);
let loaded = false;

export function useMyExams() {
  async function fetchMyExams(force = false) {
    if (loaded && !force) {
      return; // 已加载且非强制刷新，直接复用内存数据
    }
    loading.value = true;
    const { isSuccess, data } = await $fetch<RegListData>('/api/web/register/list', {
      data: { page: 1, page_size: 999 },
    });
    loading.value = false;

    if (!isSuccess || !data) {
      return;
    }

    myExams.value = (data.list ?? []).map(mapReg);
    loaded = true;
  }

  // 作废缓存：报名成功等数据变更后调用，使下次进入列表重新拉取
  function invalidate() {
    loaded = false;
  }

  return { myExams, loading, fetchMyExams, invalidate };
}
